import React, { useLayoutEffect, useRef, useState } from 'react';
import { observeElementResize } from './observeElementResize';
import { MapTopInsetContext } from './MapTopInsetContext';

/** Keep stats outside map layout and camera gestures, with room for zoom controls. */
export function GameMapFrame({ stats, children }: { stats?: React.ReactNode; children: React.ReactNode }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const [topInset, setTopInset] = useState<number | null>(stats != null ? null : 0);
  useLayoutEffect(() => {
    const frame = frameRef.current;
    const overlay = statsRef.current;
    if (!frame) return;
    if (!overlay) {
      setTopInset(0);
      frame.style.removeProperty('--game-map-controls-top');
      return;
    }
    const measure = () => {
      const statsBox = overlay.getBoundingClientRect();
      const viewport = frame.querySelector('.digital-map-viewport');
      const viewportBox = viewport?.getBoundingClientRect();
      setTopInset(statsBox.height > 0 && viewportBox ? Math.max(0, Math.ceil(statsBox.bottom - viewportBox.top) + 12) : 0);
      const controls = frame.querySelector<HTMLElement>('.digital-map-controls');
      const controlsBox = controls?.getBoundingClientRect();
      // Move controls below stats only when their horizontal ranges overlap.
      const overlaps = controlsBox && statsBox.right + 8 > controlsBox.left;
      frame.style.setProperty('--game-map-controls-top', overlaps ? `${Math.ceil(statsBox.height) + 12}px` : '8px');
    };
    measure();
    const disconnect = observeElementResize([frame, overlay, frame.querySelector('.digital-map-controls')], measure);
    window.addEventListener('resize', measure);
    return () => { disconnect(); window.removeEventListener('resize', measure); };
  }, [children, stats]);
  return <div className="game-map-frame" ref={frameRef}>
    {stats != null && <div className="game-map-stats" ref={statsRef}>{stats}</div>}
    <MapTopInsetContext.Provider value={topInset}>{children}</MapTopInsetContext.Provider>
  </div>;
}
